import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { translations } from 'db/translations.js';
import './compliance-page.js';
import './imprint-page.js';

import { FLAG_DE, FLAG_GB } from './flags.js';

import { 
  PenTool, Target, BarChart3, Folder, CheckCircle, Paperclip, Send, Plus, 
  X, FileText, Edit, ShieldCheck, Server, Globe, Lock, Mail, Gift, 
  ChevronDown, CreditCard, Clock, UserCheck, Eye, BookOpen, Laptop, HelpCircle, MonitorSmartphone, MessageSquare
} from 'lucide-static';

const OWL_LOGO = require('./assets/OwlTransparent.svg');
const APP_GRADIAS = 'https://app.gradias.ai';

@customElement('main-element')
export class MainElement extends LitElement {
  
  protected createRenderRoot() {
    return this;
  }

  @state()
  private _currentLanguage: 'en' | 'de' = 'de';

  @state()
  private _currentPath = window.location.pathname;

  @state()
  private _isMobileMenuOpen = false;

  @state()
  private _openFaqIndex: number | null = 1;

  private static _scrollLockCount = 0;
  private static _savedScrollY = 0;

  override connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', () => {
      this._currentPath = window.location.pathname;
    });
  }

  private async navigate(path: string, e?: Event) {
    if (e) e.preventDefault();
    window.history.pushState({}, '', path);
    this._currentPath = path;
    
    await this.updateComplete;
    
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  private t(group: keyof typeof translations, key: string, subkey?: string): string {
    const langIdx = this._currentLanguage === 'de' ? 0 : 1;
    const section: any = translations[group];
    if (!section) return key;
    
    const entry = subkey ? section[key]?.[subkey] : section[key];
    return entry ? entry[langIdx] : key;
  }

  private _switchLanguage() {
    this._currentLanguage = this._currentLanguage === 'en' ? 'de' : 'en';
  }

  private _isWindowsPlatform() {
    return /Windows/i.test(navigator.userAgent) || /Win/i.test(navigator.platform);
  }


private _languageToggleLabel() {
    const flagSvgString = this._currentLanguage === 'en' ? FLAG_DE : FLAG_GB;
    
    return html`
      <div class="w-5 h-5 flex items-center justify-center drop-shadow-sm [&>svg]:w-full [&>svg]:h-full">
        ${unsafeSVG(flagSvgString)}
      </div>
    `;
  }

  private static _lockScroll() {
    if (MainElement._scrollLockCount === 0) {
      MainElement._savedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${MainElement._savedScrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }
    MainElement._scrollLockCount++;
  }

  private static _unlockScroll() {
    MainElement._scrollLockCount = Math.max(0, MainElement._scrollLockCount - 1);
    if (MainElement._scrollLockCount === 0) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, MainElement._savedScrollY);
    }
  }

  private _toggleMobileMenu() {
    this._isMobileMenuOpen = !this._isMobileMenuOpen;
    if (this._isMobileMenuOpen) {
      MainElement._lockScroll();
    } else {
      MainElement._unlockScroll();
    }
  }

  private _scrollToSection(sectionId: string) {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  }

  private _navigateAndClose(e: Event, sectionId: string) {
    e.preventDefault();
    
    if (this._isMobileMenuOpen) {
      this._isMobileMenuOpen = false;
      MainElement._scrollLockCount = 0;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    }

    setTimeout(() => {
      this._scrollToSection(sectionId);
    }, 50);
  }

  private _toggleFaq(index: number) {
    this._openFaqIndex = this._openFaqIndex === index ? null : index;
  }

  // --- BUTTON HELPER ---
  private renderBtn(options: { text: unknown, variant: string, onClick: any, onDark?: boolean }) {
    let classes = "inline-flex items-center justify-center font-bold rounded-full transition-colors cursor-pointer ";
    if (options.variant === 'primary') {
      classes += "bg-[#5594CF] hover:bg-[#3d7ab6] text-white px-6 py-2.5 text-sm";
    } else if (options.variant === 'action') {
      classes += options.onDark 
        ? "bg-white text-[#5594CF] hover:bg-blue-50 px-8 py-3 text-base shadow-sm"
        : "bg-[#5594CF] text-white hover:bg-[#3d7ab6] px-8 py-3 text-base shadow-sm";
    } else if (options.variant === 'icon') {
      classes += "w-10 h-10 bg-gray-100 text-gray-600 hover:bg-gray-200 text-xl";
    }
    return html`<button @click=${options.onClick} class="${classes}">${options.text}</button>`;
  }

  // --- ROUTING RENDERER ---
  override render() {
    if (this._currentPath === '/compliance') {
      return html`<compliance-page></compliance-page>`;
    }

    if (this._currentPath === '/imprint') {
      return html`<imprint-page></imprint-page>`;
    }
    
    if (this._currentPath.startsWith('/admin')) {
      return html`
        <div class="container mx-auto px-6 py-20 text-center">
          <h1 class="text-4xl font-bold mb-8 text-[#5594CF]">Admin Area</h1>
          <p class="text-gray-600 mb-4">Adornis Data-View wurde entfernt.</p>
          <a href="/" @click=${(e: Event) => this.navigate('/', e)} class="text-[#5594CF] underline font-bold">Zurück zur Startseite</a>
        </div>
      `;
    }

    return this.renderLandingPage();
  }

  private renderLandingPage() {
    return html`
      <div class="bg-white text-gray-900 font-inter relative">

<div class="fixed top-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-5xl z-50 flex justify-center pointer-events-none">
          <nav class="pointer-events-auto w-full bg-white/70 backdrop-blur-lg shadow-lg shadow-blue-900/5 border border-white/60 rounded-full px-6 py-3 flex justify-between items-center transition-all duration-300">
            
            <div class="flex items-center space-x-3 cursor-pointer" @click=${() => window.scrollTo({top: 0, behavior: 'smooth'})}>
              <img src="${OWL_LOGO}" alt="Gradias Owl" class="w-8 h-8 object-contain" />
              <span class="text-xl font-poppins font-extrabold tracking-wide text-[#89B4D6] select-none">${this.t('navigation', 'brand')}</span>
            </div>
            
            <div class="hidden md:flex items-center space-x-6">${this._renderMenuItems()}</div>
            
            <div class="md:hidden">
              <button @click=${this._toggleMobileMenu} class="text-[#5594CF] hover:text-[#3d7ab6] focus:outline-none flex items-center justify-center w-10 h-10 bg-blue-50/50 hover:bg-blue-50 rounded-full transition-colors">
                ${this._isMobileMenuOpen 
                  ? html`<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>`
                  : html`<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>`
                }
              </button>
            </div>

          </nav>
        </div>

        ${this._isMobileMenuOpen
          ? html`
              <div class="fixed top-24 left-4 right-4 bg-white/90 backdrop-blur-xl shadow-2xl shadow-blue-900/10 border border-white/60 rounded-3xl z-40 md:hidden overflow-hidden">
                <div class="flex flex-col space-y-2 p-6 relative z-10">${this._renderMenuItems(true)}</div>
              </div>
            `
          : ''}

        ${this.renderConversationalHero()}

        <section id="features" class="px-6 py-24 bg-white relative overflow-hidden">
          <div class="container mx-auto relative z-10">
            <div class="text-center mb-16">
            <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#5594CF] font-bold text-xs uppercase tracking-widest mb-6 border border-blue-100">
                ${this.t('features', 'tag')}
              </div>
              <h2 class="text-4xl font-extrabold text-slate-800 mb-4">
                ${this.t('features', 'title')}
              </h2>
              <p class="text-lg text-gray-500 max-w-2xl mx-auto">
                ${this.t('features', 'description')}
              </p>
            </div>

            <div class="grid md:grid-cols-3 gap-8">
              ${this.renderFeature(
                this.t('features', 'handwritingRecognition', 'title'),
                this.t('features', 'handwritingRecognition', 'description'),
                PenTool
              )}
              ${this.renderFeature(
                this.t('features', 'control', 'title'),
                this.t('features', 'control', 'description'),
                Target
              )}
              ${this.renderFeature(
                this.t('features', 'analytics', 'title'),
                this.t('features', 'analytics', 'description'),
                BarChart3
              )}
            </div>
          </div>
        </section>

        <section id="how-it-works" class="px-6 py-24 bg-[#F8FAFC] border-t border-gray-100 relative overflow-hidden">
            <div class="hidden md:block absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-5 pointer-events-none z-0">
              <img src="${OWL_LOGO}" class="w-full h-full object-contain md:filter md:blur-md" />
            </div>

          <div class="container mx-auto relative z-10">
            <div class="text-center mb-20">
            <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#5594CF] font-bold text-xs uppercase tracking-widest mb-6 border border-blue-100">
                ${this.t('howItWorks', 'tag')}
              </div>
              <h2 class="text-4xl font-extrabold text-slate-800 mb-4">
                ${this.t('howItWorks', 'title')}
              </h2>
              <p class="text-lg text-gray-500 max-w-2xl mx-auto">${this.t('howItWorks', 'description')}</p>
            </div>

            <div class="space-y-24">
              ${this.renderProcessStep(1, this.t('howItWorks', 'step1', 'title'), this.t('howItWorks', 'step1', 'description'), this.renderMockupUpload(), false)}
              ${this.renderProcessStep(2, this.t('howItWorks', 'step2', 'title'), this.t('howItWorks', 'step2', 'description'), this.renderMockupMasterSolution(), true)}
              ${this.renderProcessStep(3, this.t('howItWorks', 'step3', 'title'), this.t('howItWorks', 'step3', 'description'), this.renderMockupGradingScheme(), false)}
              ${this.renderProcessStep(4, this.t('howItWorks', 'step4', 'title'), this.t('howItWorks', 'step4', 'description'), this.renderMockupDetailedCorrection(), true)}
              ${this.renderProcessStep(5, this.t('howItWorks', 'step5', 'title'), this.t('howItWorks', 'step5', 'description'), this.renderMockupSummary(), false)}
            </div>
          </div>
        </section>

        <section id="use-cases" class="px-6 py-24 bg-white border-t border-gray-100 relative overflow-hidden">
          <div class="container mx-auto relative z-10">
            <div class="text-center mb-16">
              <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#5594CF] font-bold text-xs uppercase tracking-widest mb-6 border border-blue-100">
                ${this.t('useCases', 'tag')}
              </div>
              <h2 class="text-4xl font-extrabold text-slate-800 mb-4">
                ${this.t('useCases', 'title')}
              </h2>
              <p class="text-lg text-gray-500 max-w-2xl mx-auto">
                ${unsafeHTML(this.t('useCases', 'description'))}
              </p>
            </div>

            <div class="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              
              <div class="bg-[#F8FAFC] p-8 rounded-2xl shadow-sm border border-gray-100 transition-all hover:shadow-md">
                <div class="w-12 h-12 bg-white shadow-sm text-green-500 rounded-xl flex items-center justify-center mb-6 [&>svg]:w-6 [&>svg]:h-6 border border-gray-100">
                  ${unsafeSVG(CheckCircle)}
                </div>
                <h3 class="text-2xl font-bold text-slate-800 mb-4">${this.t('useCases', 'worksPerfectTitle')}</h3>
                <p class="text-gray-600 mb-4 text-sm leading-relaxed">${this.t('useCases', 'worksPerfectDesc')}</p>
                <ul class="space-y-3 text-gray-600">
                  <li class="flex items-start gap-3"><div class="text-[#5594CF] mt-0.5 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(FileText)}</div> <span>${unsafeHTML(this.t('useCases', 'worksPerfect1'))}</span></li>
                  <li class="flex items-start gap-3"><div class="text-[#5594CF] mt-0.5 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(FileText)}</div> <span>${unsafeHTML(this.t('useCases', 'worksPerfect2'))}</span></li>
                  <li class="flex items-start gap-3"><div class="text-[#5594CF] mt-0.5 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(FileText)}</div> <span>${unsafeHTML(this.t('useCases', 'worksPerfect3'))}</span></li>
                </ul>
              </div>

              <div class="bg-[#F8FAFC] p-8 rounded-2xl shadow-sm border border-gray-100 transition-all hover:shadow-md">
                <div class="w-12 h-12 bg-white shadow-sm text-gray-400 rounded-xl flex items-center justify-center mb-6 [&>svg]:w-6 [&>svg]:h-6 border border-gray-100">
                  ${unsafeSVG(Target)} 
                </div>
                <h3 class="text-2xl font-bold text-slate-800 mb-4">${this.t('useCases', 'comesLaterTitle')}</h3>
                <p class="text-gray-600 mb-4 text-sm leading-relaxed">${this.t('useCases', 'comesLaterDesc')}</p>
                <ul class="space-y-3 text-gray-400 font-medium">
                  <li class="flex items-center gap-3"><div class="text-gray-300 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(X)}</div> ${this.t('useCases', 'comesLater1')}</li>
                  <li class="flex items-center gap-3"><div class="text-gray-300 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(X)}</div> ${this.t('useCases', 'comesLater2')}</li>
                  <li class="flex items-center gap-3"><div class="text-gray-300 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(X)}</div> ${this.t('useCases', 'comesLater3')}</li>
                </ul>
              </div>

            </div>
          </div>
        </section>

        <section id="roadmap" class="px-6 py-24 bg-[#F8FAFC] border-t border-gray-100 relative overflow-hidden">
          <div class="container mx-auto relative z-10">
            <div class="max-w-5xl mx-auto">
              <div class="text-center mb-16">
                <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 text-orange-600 font-bold text-xs uppercase tracking-widest mb-6 border border-orange-100">
                  ${this.t('roadmap', 'tag')}
                </div>
                <h2 class="text-4xl font-extrabold text-slate-800 mb-4">
                  ${this.t('roadmap', 'title')}
                </h2>
                <p class="text-lg text-gray-500 max-w-2xl mx-auto">
                  ${unsafeHTML(this.t('roadmap', 'description'))}
                </p>
              </div>

              <div class="grid md:grid-cols-3 gap-6 relative">
                
                <div class="hidden md:block absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2 text-gray-300 [&>svg]:w-6 [&>svg]:h-6 z-0">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
                </div>
                <div class="hidden md:block absolute top-1/2 left-2/3 -translate-x-1/2 -translate-y-1/2 text-gray-300 [&>svg]:w-6 [&>svg]:h-6 z-0">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
                </div>

                <div class="p-6 rounded-2xl border border-blue-200 bg-blue-50/30 shadow-md ring-1 ring-blue-500/20 transform md:-translate-y-2 relative z-10">
                  <div class="flex items-center gap-2 mb-4">
                    <div class="flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] font-bold">1</div>
                    <span class="text-xs font-bold text-blue-600 uppercase tracking-wide">${this.t('roadmap', 'phase1Tag')}</span>
                  </div>
                  <h4 class="font-bold text-slate-800 mb-2">${this.t('roadmap', 'phase1Title')}</h4>
                  <p class="text-sm text-gray-600 leading-relaxed">${this.t('roadmap', 'phase1Desc')}</p>
                </div>

                <div class="p-6 rounded-2xl border border-gray-200 bg-white shadow-sm opacity-90 hover:opacity-100 transition-opacity relative z-10">
                  <div class="flex items-center gap-2 mb-4">
                    <div class="flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold">2</div>
                    <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">${this.t('roadmap', 'phase2Tag')}</span>
                  </div>
                  <h4 class="font-bold text-slate-800 mb-2">${this.t('roadmap', 'phase2Title')}</h4>
                  <p class="text-sm text-gray-600 leading-relaxed">${this.t('roadmap', 'phase2Desc')}</p>
                </div>

                <div class="p-6 rounded-2xl border border-gray-200 bg-white shadow-sm opacity-90 hover:opacity-100 transition-opacity relative z-10">
                  <div class="flex items-center gap-2 mb-4">
                    <div class="flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold">3</div>
                    <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">${this.t('roadmap', 'phase3Tag')}</span>
                  </div>
                  <h4 class="font-bold text-slate-800 mb-2">${this.t('roadmap', 'phase3Title')}</h4>
                  <p class="text-sm text-gray-600 leading-relaxed">${this.t('roadmap', 'phase3Desc')}</p>
                </div>

              </div>
            </div>
          </div>
        </section>

        <section id="pricing" class="px-6 py-24 bg-white border-t border-gray-100 relative overflow-hidden">
          <div class="container mx-auto relative z-10">
            <div class="text-center mb-16">
            <div class="inline-flex items-center justify-center w-16 h-16 bg-blue-50 shadow-sm border border-blue-100 text-[#5594CF] rounded-full mb-6 [&>svg]:w-7 [&>svg]:h-7">
                ${unsafeSVG(CreditCard)}
              </div>
              <h2 class="text-4xl font-extrabold text-slate-800 mb-4">
                ${this.t('pricing', 'title')}
              </h2>
              <p class="text-lg text-gray-500 max-w-2xl mx-auto">
                ${this.t('pricing', 'description')}
              </p>
            </div>

            <div class="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto items-stretch">
              
              <div class="bg-white p-8 rounded-3xl border-2 border-[#5594CF] shadow-lg flex flex-col h-full transform md:-translate-y-2 relative z-10">
                <div class="mb-4">
                  <span class="bg-blue-100 text-[#5594CF] text-[10px] font-bold px-3 py-1.5 uppercase tracking-widest rounded-full">
                    ${this.t('pricing', 'pilotTag')}
                  </span>
                </div>
                <h3 class="text-2xl font-bold text-slate-800 mb-2">${this.t('pricing', 'pilotTitle')}</h3>
                <div class="flex items-baseline gap-1 mb-8">
                  <span class="text-5xl font-extrabold text-slate-900">0€</span>
                  <span class="text-gray-500 font-medium">${this.t('pricing', 'perMonth')}</span>
                </div>
                <ul class="space-y-4 mb-8 flex-1 text-sm text-gray-600">
                  <li class="flex items-start gap-3"><div class="text-green-500 mt-0.5 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(CheckCircle)}</div> <span>${this.t('pricing', 'pilot1')}</span></li>
                  <li class="flex items-start gap-3"><div class="text-green-500 mt-0.5 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(CheckCircle)}</div> <span>${unsafeHTML(this.t('pricing', 'pilot2'))}</span></li>
                  <li class="flex items-start gap-3"><div class="text-green-500 mt-0.5 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(CheckCircle)}</div> <span>${this.t('pricing', 'pilot3')}</span></li>
                </ul>
                
                <div class="mt-auto w-full flex flex-col">
                  <div class="mb-6 w-full p-3.5 bg-white border border-blue-100 rounded-xl flex items-center gap-3 shadow-sm">
                    <div class="w-5 h-5 text-[#5594CF] flex-shrink-0 [&>svg]:w-full [&>svg]:h-full">
                      ${unsafeSVG(Gift)}
                    </div>
                    <span class="text-xs font-semibold text-[#5594CF] text-left leading-snug">
                      ${this.t('pricing', 'pilotBonus')}
                    </span>
                  </div>
                  
                  <div class="flex justify-center w-full mb-3">
                    ${this.renderBtn({
                      text: this.t('pricing', 'pilotBtn'),
                      variant: 'primary',
                      onClick: () => window.open(APP_GRADIAS, '_blank'),
                    })}
                  </div>

                  <span class="text-[10px] text-gray-400 text-center font-medium px-2 leading-tight">
                    ${this.t('pricing', 'noAbo')}
                  </span>
                </div>
              </div>

              <div class="bg-slate-50 p-8 rounded-3xl border border-gray-100 flex flex-col h-full opacity-70">
                <div class="mb-4 h-[26px]"></div> 
                <h3 class="text-2xl font-bold text-slate-800 mb-2">${this.t('pricing', 'proTitle')}</h3>
                <div class="flex items-baseline gap-2 mb-8">
                  <span class="text-3xl font-bold text-slate-400 italic">${this.t('pricing', 'comingSoon')}</span>
                </div>
                <ul class="space-y-4 mb-8 flex-1 text-sm text-gray-500">
                  <li class="flex items-start gap-3"><div class="text-gray-300 mt-0.5 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(CheckCircle)}</div> <span>${this.t('pricing', 'pro1')}</span></li>
                  <li class="flex items-start gap-3"><div class="text-gray-300 mt-0.5 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(CheckCircle)}</div> <span>${this.t('pricing', 'pro2')}</span></li>
                  <li class="flex items-start gap-3"><div class="text-gray-300 mt-0.5 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(CheckCircle)}</div> <span>${this.t('pricing', 'pro3')}</span></li>
                </ul>
                <div class="mt-auto w-full flex justify-center">
                  <div class="px-6 py-2.5 bg-white border border-gray-200 text-gray-400 font-bold rounded-full text-sm">
                    ${this.t('pricing', 'proBtn')}
                  </div>
                </div>
              </div>

              <div class="bg-slate-50 p-8 rounded-3xl border border-gray-100 flex flex-col h-full opacity-70">
                <div class="mb-4 h-[26px]"></div> 
                <h3 class="text-2xl font-bold text-slate-800 mb-2">${this.t('pricing', 'schoolTitle')}</h3>
                <div class="flex items-baseline gap-2 mb-8">
                  <span class="text-3xl font-bold text-slate-400 italic">${this.t('pricing', 'comingSoon')}</span>
                </div>
                <ul class="space-y-4 mb-8 flex-1 text-sm text-gray-500">
                  <li class="flex items-start gap-3"><div class="text-gray-300 mt-0.5 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(CheckCircle)}</div> <span>${this.t('pricing', 'school1')}</span></li>
                  <li class="flex items-start gap-3"><div class="text-gray-300 mt-0.5 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(CheckCircle)}</div> <span>${this.t('pricing', 'school2')}</span></li>
                  <li class="flex items-start gap-3"><div class="text-gray-300 mt-0.5 [&>svg]:w-5 [&>svg]:h-5">${unsafeSVG(CheckCircle)}</div> <span>${this.t('pricing', 'school3')}</span></li>
                </ul>
                <div class="mt-auto w-full flex justify-center">
                  <div class="px-6 py-2.5 bg-white border border-gray-200 text-gray-400 font-bold rounded-full text-sm">
                    ${this.t('pricing', 'schoolBtn')}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        <section id="faq" class="px-6 py-24 bg-[#F8FAFC] border-t border-gray-100 relative overflow-hidden">
          <div class="container mx-auto relative z-10 max-w-3xl">
            <div class="text-center mb-16">
              <div class="inline-flex items-center justify-center w-16 h-16 bg-blue-50 shadow-sm border border-blue-100 text-[#5594CF] rounded-full mb-6 [&>svg]:w-7 [&>svg]:h-7">
                ${unsafeSVG(HelpCircle)}
              </div>
              <h2 class="text-4xl font-extrabold text-slate-800 mb-4">
                ${this.t('faq', 'title')}
              </h2>
              <p class="text-lg text-gray-500 mb-4">
                ${this.t('faq', 'subtitle1')}
              </p>
            </div>

            <div class="border-t border-gray-200">
                ${this.renderFaqItem(1, this.t('faq', 'q1'), this.t('faq', 'a1'), CreditCard)}
                ${this.renderFaqItem(2, this.t('faq', 'q2'), this.t('faq', 'a2'), Clock)}
                ${this.renderFaqItem(3, this.t('faq', 'q3'), this.t('faq', 'a3'), ShieldCheck)}
                ${this.renderFaqItem(4, this.t('faq', 'q4'), this.t('faq', 'a4'), UserCheck)}
                ${this.renderFaqItem(5, this.t('faq', 'q5'), this.t('faq', 'a5'), Eye)}
                ${this.renderFaqItem(6, this.t('faq', 'q6'), this.t('faq', 'a6'), BookOpen)}
                ${this.renderFaqItem(7, this.t('faq', 'q7'), this.t('faq', 'a7'), Laptop)}
                ${this.renderFaqItem(8, this.t('faq', 'q8'), this.t('faq', 'a8'), FileText)}
                ${this.renderFaqItem(9, this.t('faq', 'q9'), this.t('faq', 'a9'), MonitorSmartphone)}
                ${this.renderFaqItem(10, this.t('faq', 'q10'), this.t('faq', 'a10'), Send)}
                ${this.renderFaqItem(11, this.t('faq', 'q11'), this.t('faq', 'a11'), MessageSquare)}
            </div>
          </div>
        </section>

        <section id="privacy" class="px-6 py-24 bg-white border-t border-gray-100 relative overflow-hidden">
          <div class="container mx-auto relative z-10">
            <div class="max-w-5xl mx-auto">
              
              <div class="text-center mb-16">
                <div class="inline-flex items-center justify-center p-4 bg-blue-50 rounded-full mb-6">
                   <div class="w-10 h-10 text-[#5594CF] [&>svg]:w-full [&>svg]:h-full">${unsafeSVG(ShieldCheck)}</div>
                </div>
                <h2 class="text-4xl font-extrabold text-slate-800 mb-4">
                  ${this.t('privacySection', 'title')}
                </h2>
                <p class="text-lg text-gray-500 max-w-2xl mx-auto">
                  ${this.t('privacySection', 'description')}
                </p>
              </div>

              <div class="grid md:grid-cols-3 gap-8 mb-12">
                <div class="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                  <div class="w-10 h-10 text-[#5594CF] mb-6 [&>svg]:w-full [&>svg]:h-full">${unsafeSVG(Server)}</div>
                  <h3 class="text-xl font-bold text-slate-800 mb-3">${this.t('privacySection', 'hostingTitle')}</h3>
                  <p class="text-gray-600 leading-relaxed text-sm">
                    ${this.t('privacySection', 'hostingDesc')}
                  </p>
                </div>

                <div class="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                  <div class="w-10 h-10 text-[#5594CF] mb-6 [&>svg]:w-full [&>svg]:h-full">${unsafeSVG(Globe)}</div>
                  <h3 class="text-xl font-bold text-slate-800 mb-3">${this.t('privacySection', 'euAiTitle')}</h3>
                  <p class="text-gray-600 leading-relaxed text-sm">
                    ${this.t('privacySection', 'euAiDesc')}
                  </p>
                </div>

                <div class="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                  <div class="w-10 h-10 text-[#5594CF] mb-6 [&>svg]:w-full [&>svg]:h-full">${unsafeSVG(Lock)}</div>
                  <h3 class="text-xl font-bold text-slate-800 mb-3">${this.t('privacySection', 'noTrainingTitle')}</h3>
                  <p class="text-gray-600 leading-relaxed text-sm">
                    ${this.t('privacySection', 'noTrainingDesc')}
                  </p>
                </div>
              </div>

              <div class="text-center">
                <a href="/compliance" @click=${(e: Event) => this.navigate('/compliance', e)} class="inline-flex items-center justify-center px-8 py-4 text-sm font-bold text-[#5594CF] bg-blue-50 hover:bg-blue-100 border border-blue-100 rounded-xl transition-colors duration-200 gap-2">
                  ${this.t('privacySection', 'readMore')} &rarr;
                </a>
              </div>
              
            </div>
          </div>
        </section>

        <section id="cta" class="bg-[#89B4D6] text-white py-24 text-center relative overflow-hidden">
          <div class="container mx-auto px-6 relative z-10">
            <div class="max-w-3xl mx-auto text-white">
              <img src="${OWL_LOGO}" alt="Owl" class="w-16 h-16 mx-auto mb-6 object-contain filter brightness-0 invert opacity-90" />
              <h2 class="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight">
                ${this.t('cta', 'title')}
              </h2>
              <p class="text-xl mb-10 text-white/90 leading-relaxed">
                ${this.t('cta', 'description')}
              </p>
              
              <div class="flex flex-col sm:flex-row justify-center items-center gap-4">
                ${this.renderBtn({
                  text: this.t('cta', 'getStarted'),
                  variant: 'action',
                  onDark: true,
                  onClick: () => window.open(APP_GRADIAS, '_blank', 'noopener,noreferrer'),
                })}
              </div>
            </div>
          </div>
        </section>

        <footer class="bg-slate-900 py-12 text-center text-sm text-slate-400">
          <div class="mx-auto px-6">
            <div class="flex justify-center space-x-6 mb-6">
              <a href="/imprint" @click=${(e: Event) => this.navigate('/imprint', e)} class="hover:text-white transition-colors">
                ${this.t('footer', 'imprint')}
              </a>
              <a href="/compliance" @click=${(e: Event) => this.navigate('/compliance', e)} class="hover:text-white transition-colors">
                ${this.t('footer', 'dataPrivacy')}
              </a>
            </div>
            <p>${this.t('footer', 'copyright')}</p>
          </div>
        </footer>
      </div>
    `;
  }

private _renderMenuItems(isMobile = false) {
    const menuItemClasses = isMobile 
      ? 'text-gray-600 font-medium hover:text-[#5594CF] py-2 w-full text-left' 
      : 'text-gray-600 font-medium hover:text-[#5594CF] transition-colors';
    const languageBadgeStyle = this._isWindowsPlatform()
      ? "font-family: 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji', sans-serif; line-height: 1;"
      : 'line-height: 1;';

    return html`
      ${this.renderBtn({
        text: html`<span style="${languageBadgeStyle}">${this._languageToggleLabel()}</span>`,
        variant: 'icon',
        onClick: () => {
          this._switchLanguage();
          if (isMobile) this._toggleMobileMenu();
        },
      })}

      <a href="#how-it-works" class=${menuItemClasses} @click=${(e: Event) => this._navigateAndClose(e, 'how-it-works')}>
        ${this.t('navigation', 'howItWorks')}
      </a>

      <a href="#pricing" class=${menuItemClasses} @click=${(e: Event) => this._navigateAndClose(e, 'pricing')}>
        ${this.t('navigation', 'pricing')}
      </a>

      <a href="#faq" class=${menuItemClasses} @click=${(e: Event) => this._navigateAndClose(e, 'faq')}>
        ${this.t('navigation', 'faq')}
      </a>

      <a href="/compliance" class=${menuItemClasses} @click=${(e: Event) => { this.navigate('/compliance', e); if (isMobile) this._toggleMobileMenu(); }}>
        ${this.t('navigation', 'privacy')}
      </a>

      ${this.renderBtn({
        text: this.t('navigation', 'getStarted'),
        variant: 'primary',
        onClick: () => window.open(APP_GRADIAS, '_blank', 'noopener,noreferrer'),
      })}
    `;
  }

  private renderFeature(title: string, description: string, iconSvg: string) {
    return html`
      <div class="bg-[#F8FAFC] border border-gray-100 rounded-2xl p-8 text-left md:transition-all md:hover:-translate-y-1 md:hover:shadow-lg relative overflow-hidden group">
        <div class="w-10 h-10 mb-4 text-[#5594CF] md:transition-transform md:group-hover:scale-110 md:duration-300 [&>svg]:w-full [&>svg]:h-full">
          ${unsafeSVG(iconSvg)}
        </div>
        <h3 class="text-xl font-bold mb-3 text-slate-800 relative z-10">${title}</h3>
        <p class="text-gray-600 leading-relaxed text-sm relative z-10">${description}</p>
      </div>
    `;
  }

  private renderProcessStep(stepNumber: number, title: string, description: string, mockupHtml: any, reverse: boolean) {
    return html`
      <div class="flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12 lg:gap-20">
        <div class="flex-1 text-center lg:text-left">
          <div class="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-[#5594CF] font-bold text-xl mb-6">
            ${stepNumber}
          </div>
          <h3 class="text-3xl font-bold text-slate-800 mb-4">${title}</h3>
          <p class="text-lg text-gray-600 leading-relaxed">${description}</p>
        </div>
        <div class="flex-1 w-full max-w-xl">
          <div class="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden md:transform md:transition-all md:hover:scale-[1.02]">
            ${mockupHtml}
          </div>
        </div>
      </div>
    `;
  }

  private renderMockupUpload() {
    return html`
      <div class="p-6 bg-white flex flex-col gap-4">
        <div class="flex gap-4 items-start">
            <div class="w-8 h-8 rounded-full bg-[#EAF3FB] flex items-center justify-center flex-shrink-0 mt-1 p-1.5">
              <img src="${OWL_LOGO}" class="w-full h-full object-contain" />
            </div>
            <div class="bg-[#F6FAFE] border border-blue-50 text-slate-700 rounded-2xl rounded-tl-sm py-3 px-5 text-sm leading-relaxed w-full">
              ${this.t('mockups', 'upload', 'msg')}
            </div>
        </div>
        <div class="ml-12 border border-gray-100 rounded-xl p-4 shadow-sm">
            <div class="text-xs font-bold text-gray-400 mb-3 tracking-wider uppercase flex justify-between">
              <span>${this.t('mockups', 'upload', 'status')}</span> <span>25%</span>
            </div>
            <div class="space-y-3">
                <div class="flex items-center gap-2 text-sm text-gray-400">
                  <div class="w-4 h-4 text-green-500 [&>svg]:w-full [&>svg]:h-full flex-shrink-0">${unsafeSVG(CheckCircle)}</div>
                  ${this.t('mockups', 'upload', 'recognized')}
                </div>
                <div class="flex items-center gap-2 text-sm text-slate-700 font-medium">
                    <div class="w-3 h-3 border-2 border-[#5594CF] border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                    ${this.t('mockups', 'upload', 'extracting')}
                </div>
            </div>
        </div>
      </div>
    `;
  }

  private renderMockupMasterSolution() {
    return html`
      <div class="flex flex-col w-full text-sm bg-gray-50 h-[350px]">
        <div class="p-4 border-b border-gray-100 bg-white flex justify-between items-center font-semibold text-slate-800">
          ${this.t('mockups', 'masterSolution', 'title')} 
          <div class="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors [&>svg]:w-full [&>svg]:h-full">
            ${unsafeSVG(X)}
          </div>
        </div>
        <div class="p-4 overflow-y-auto space-y-4">
          <div class="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
            <div class="text-xs font-semibold text-gray-500 mb-2 flex justify-between items-center">
              <span class="flex items-center gap-1.5">
                <div class="w-3.5 h-3.5 text-gray-400 [&>svg]:w-full [&>svg]:h-full">${unsafeSVG(FileText)}</div>
                ${this.t('mockups', 'masterSolution', 'snippet')}
              </span>
              <span class="bg-gray-100 text-gray-500 px-2 py-0.5 rounded text-[10px] uppercase tracking-wide">${this.t('mockups', 'masterSolution', 'task6')}</span>
            </div>
            <div class="h-16 bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400 italic rounded text-xs text-center px-4">
              ${this.t('mockups', 'masterSolution', 'text1')}
            </div>
          </div>
          <div class="bg-blue-50/50 border border-blue-100 rounded-lg p-3 shadow-sm">
             <div class="flex justify-between items-center mb-3">
               <span class="text-[#5594CF] font-semibold flex items-center gap-2">
                 <img src="${OWL_LOGO}" class="w-4 h-4 object-contain" /> ${this.t('mockups', 'masterSolution', 'suggestion')}
               </span>
               <button class="bg-white border border-blue-200 text-[#5594CF] text-xs px-3 py-1 rounded-full font-medium shadow-sm flex items-center gap-1.5 hover:bg-blue-50 transition-colors">
                 <div class="w-3 h-3 [&>svg]:w-full [&>svg]:h-full">${unsafeSVG(Edit)}</div>
                 ${this.t('mockups', 'masterSolution', 'edit')}
               </button>
             </div>
             <p class="text-slate-700 leading-relaxed text-xs">${this.t('mockups', 'masterSolution', 'text2')}</p>
          </div>
        </div>
      </div>
    `;
  }

  private renderMockupGradingScheme() {
    return html`
      <div class="flex flex-col w-full text-sm bg-gray-50 h-[350px]">
        <div class="p-4 border-b border-gray-100 bg-white flex justify-between items-center font-semibold text-slate-800">
          ${this.t('mockups', 'gradingScheme', 'title')}
        </div>
        <div class="p-4 overflow-y-auto space-y-4">
          <div class="bg-purple-50/30 border border-purple-100 rounded-lg p-4 shadow-sm">
             <div class="flex justify-between items-center mb-4">
               <span class="text-purple-600 font-semibold flex items-center gap-2">
                 <img src="${OWL_LOGO}" class="w-4 h-4 object-contain" /> ${this.t('mockups', 'gradingScheme', 'suggestion')}
               </span>
               <span class="bg-purple-100 text-purple-700 text-xs font-bold px-3 py-1 rounded-full">${this.t('mockups', 'gradingScheme', 'maxPoints')}</span>
             </div>
             <ul class="space-y-3 text-slate-700 text-xs">
               <li class="flex gap-2"><b>1 P.</b> <span>${this.t('mockups', 'gradingScheme', 'p1')}</span></li>
               <li class="flex gap-2"><b>2 P.</b> <span>${this.t('mockups', 'gradingScheme', 'p2')}</span></li>
               <li class="flex gap-2"><b>2 P.</b> <span>${this.t('mockups', 'gradingScheme', 'p3')}</span></li>
             </ul>
          </div>
        </div>
      </div>
    `;
  }

  private renderMockupDetailedCorrection() {
    return html`
      <div class="flex flex-col w-full text-sm bg-gray-50 h-[350px]">
        <div class="p-4 border-b border-gray-100 bg-white flex justify-between items-center font-semibold text-slate-800">
          ${this.t('mockups', 'correction', 'title')}
        </div>
        <div class="p-4 overflow-y-auto space-y-4">
          <div class="flex justify-between items-center bg-white border border-gray-200 rounded-lg p-2 shadow-sm">
            <span class="text-xs font-semibold px-2">${this.t('mockups', 'correction', 'task1')}</span>
            <span class="text-xs text-gray-500 font-medium">${this.t('mockups', 'correction', 'studentInfo')}</span>
          </div>
          <div class="bg-white border border-gray-200 rounded-lg p-3 h-32 relative shadow-sm overflow-hidden">
             <div class="h-full w-full rounded bg-[#F8FAFC] border border-gray-100"></div>
             <div
               class="absolute top-6 left-14 text-slate-700 select-none"
               style="font-family: 'Comic Sans MS', 'Bradley Hand', 'Segoe Print', cursive;"
             >
               <div class="text-[12px] font-medium rotate-[-4deg] leading-4 tracking-[0.01em]">3x + 5 = 20</div>
               <div class="text-[11px] font-medium rotate-[-2deg] ml-1 mt-0.5 leading-4">3x = 15</div>
               <div class="text-[12px] font-semibold rotate-[-3deg] mt-0.5">x = 5 ✓</div>
             </div>
             <div class="absolute top-5 left-12 w-28 h-[58px] border-2 border-[#89B4D6] bg-[#89B4D6]/10 rounded-md shadow-sm"></div>
             <div class="absolute top-2 left-12 text-[10px] font-semibold text-[#5594CF] bg-white px-1 rounded">Bounding Box</div>
          </div>
          <div class="bg-green-50 border border-green-200 rounded-lg p-3 shadow-sm">
             <div class="flex justify-between items-center mb-2">
               <span class="text-green-700 font-bold text-xs uppercase tracking-wide">${this.t('mockups', 'correction', 'grading')}</span>
               <span class="bg-green-100 text-green-800 text-xs font-bold px-2 py-0.5 rounded">${this.t('mockups', 'correction', 'points')}</span>
             </div>
             <p class="text-green-800/80 italic text-xs">${this.t('mockups', 'correction', 'feedback')}</p>
          </div>
        </div>
      </div>
    `;
  }

  private renderMockupSummary() {
    return html`
      <div class="flex flex-col w-full text-sm bg-gray-50 h-[350px]">
        <div class="p-4 border-b border-gray-100 bg-white flex justify-between items-center font-semibold text-slate-800">
          ${this.t('mockups', 'summary', 'title')}
        </div>
        <div class="p-4 overflow-y-auto space-y-3">
          <div class="grid grid-cols-2 gap-3">
             <div class="bg-white border border-gray-200 rounded-lg p-3 shadow-sm text-center">
               <div class="text-[10px] font-bold text-gray-500 uppercase mb-1">${this.t('mockups', 'summary', 'average')}</div>
               <div class="text-xl font-bold text-slate-800">2.4</div>
             </div>
             <div class="bg-white border border-gray-200 rounded-lg p-3 shadow-sm text-center">
               <div class="text-[10px] font-bold text-gray-500 uppercase mb-1">${this.t('mockups', 'summary', 'avgPoints')}</div>
               <div class="text-xl font-bold text-[#5594CF]">74 <span class="text-xs font-normal text-gray-400">/ 92</span></div>
             </div>
          </div>
          <div class="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
             <div class="font-semibold text-slate-700 mb-3 text-xs">${this.t('mockups', 'summary', 'studentSummary')}</div>
             <table class="w-full text-left text-xs">
               <tr class="text-gray-400 border-b border-gray-100"><th class="pb-2 font-medium">${this.t('mockups', 'summary', 'name')}</th><th class="pb-2 font-medium">A1</th><th class="pb-2 font-medium">A2</th><th class="pb-2 font-medium text-right">${this.t('mockups', 'summary', 'total')}</th></tr>
               <tr class="border-b border-gray-50"><td class="py-2 text-slate-700">${this.t('mockups', 'summary', 'student1')}</td><td class="py-2 text-green-600">1/1</td><td class="py-2 text-red-500">2/5</td><td class="py-2 text-right font-bold text-slate-700">76 P.</td></tr>
               <tr class="border-b border-gray-50"><td class="py-2 text-slate-700">${this.t('mockups', 'summary', 'student2')}</td><td class="py-2 text-red-500">0/1</td><td class="py-2 text-green-600">5/5</td><td class="py-2 text-right font-bold text-slate-700">82 P.</td></tr>
               <tr><td class="py-2 text-slate-700">${this.t('mockups', 'summary', 'student3')}</td><td class="py-2 text-green-600">1/1</td><td class="py-2 text-yellow-600">3/5</td><td class="py-2 text-right font-bold text-slate-700">65 P.</td></tr>
             </table>
          </div>
        </div>
      </div>
    `;
  }

  private renderFaqItem(index: number, question: string, answer: string, iconSvg: string) {
    const isOpen = this._openFaqIndex === index;
    return html`
      <div class="border-b border-gray-200 py-6 last:border-0">
        <button 
          class="w-full flex items-center justify-between text-left focus:outline-none group" 
          @click=${() => this._toggleFaq(index)}
        >
          <div class="flex items-center gap-4">
            <div class="w-10 h-10 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-500 group-hover:text-[#5594CF] transition-colors flex-shrink-0 [&>svg]:w-5 [&>svg]:h-5">
              ${unsafeSVG(iconSvg)}
            </div>
            <span class="text-lg font-bold text-slate-800">${question}</span>
          </div>
          <div class="text-gray-400 [&>svg]:w-5 [&>svg]:h-5 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#5594CF]' : ''}">
            ${unsafeSVG(ChevronDown)}
          </div>
        </button>
        ${isOpen ? html`
          <div class="mt-4 pl-14 pr-8 animate-fade-in-up">
            <p class="text-gray-600 leading-relaxed">${unsafeHTML(answer)}</p>
          </div>
        ` : ''}
      </div>
    `;
  }

  private renderConversationalHero() {
    return html`
      <header class="relative pt-32 pb-20 lg:pt-48 lg:pb-32 bg-[#F8FAFC] overflow-hidden">
        
       <div class="absolute inset-0 z-0 hidden md:flex items-center justify-center pointer-events-none overflow-hidden">
          <img src="${OWL_LOGO}" class="w-[800px] lg:w-[1000px] h-[800px] lg:h-[1000px] opacity-5 transform translate-x-1/4 object-contain" />
        </div>

        <div class="container mx-auto px-6 relative z-10">
          <div class="flex flex-col lg:flex-row items-center gap-16">
            
            <div class="flex-1 text-center lg:text-left z-20">
              <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#5594CF] font-semibold text-sm mb-6 border border-blue-100 relative z-10">
                <img src="${OWL_LOGO}" class="w-4 h-4 object-contain" />
                ${this.t('hero', 'tag')}
              </div>
              <h1 class="text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6 leading-tight tracking-tight relative z-10">
                ${this.t('hero', 'title')}
              </h1>
              <p class="text-xl text-gray-600 mb-10 max-w-2xl mx-auto lg:mx-0 relative z-10">
                ${this.t('hero', 'description')}
              </p>
              
              <div class="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start relative z-10">
                ${this.renderBtn({
                  text: this.t('hero', 'startNow'),
                  variant: 'action',
                  onClick: () => window.open(APP_GRADIAS, '_blank', 'noopener,noreferrer'),
                })}
              </div>
            </div>

            <div class="flex-1 w-full max-w-2xl lg:max-w-none relative animate-fade-in-up z-20">
              <div class="hidden md:block absolute -inset-4 bg-gradient-to-tr from-[#89B4D6]/40 to-[#5594CF]/20 md:blur-2xl rounded-full -z-10"></div>

              <div class="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex h-[500px] md:h-[600px] w-full md:transform md:transition-transform md:hover:scale-[1.02] md:duration-500">
                
                <div class="hidden sm:flex w-64 bg-[#89B4D6] flex-col p-4 text-white">
                  <div class="flex items-center gap-2 mb-8 mt-2">
                    <span class="font-extrabold text-xl tracking-wide">${this.t('navigation', 'brand')}</span>
                  </div>
                  <button class="bg-white/20 hover:bg-white/30 transition-colors rounded-lg py-2.5 px-4 flex items-center gap-2 text-sm font-semibold mb-8">
                    <div class="w-4 h-4 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full">${unsafeSVG(Plus)}</div> 
                    ${this.t('mockupChat', 'newExam')}
                  </button>
                  <div class="text-xs font-bold uppercase tracking-wider text-white/70 mb-3">${this.t('mockupChat', 'thisYear')}</div>
                  <div class="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-md p-2 cursor-pointer text-sm transition-colors">
                    <div class="w-4 h-4 text-white/90 flex-shrink-0 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full">${unsafeSVG(Folder)}</div> 
                    <span class="truncate">${this.t('mockupChat', 'mathFolder')}</span>
                  </div>
                </div>

                <div class="flex-1 flex flex-col bg-white">
                  <div class="flex-1 p-6 overflow-y-auto flex flex-col gap-6">
                    <div class="flex justify-end gap-4 w-full">
                      <div class="bg-slate-900 text-white rounded-2xl rounded-tr-sm py-3 px-5 text-sm font-medium shadow-md">
                        ${this.t('mockupChat', 'prompt1')}
                      </div>
                    </div>

                    <div class="flex gap-4 max-w-[90%] mt-2">
                      <div class="w-8 h-8 rounded-full bg-[#EAF3FB] flex items-center justify-center flex-shrink-0 p-1.5">
                         <img src="${OWL_LOGO}" class="w-full h-full object-contain" />
                      </div>
                      <div class="bg-[#F6FAFE] border border-blue-50 text-slate-700 rounded-2xl rounded-tl-sm py-3 px-5 text-sm leading-relaxed">
                        ${this.t('mockupChat', 'reply1')}
                      </div>
                    </div>

                    <div class="flex justify-end gap-4 w-full mt-4">
                      <div class="bg-slate-900 text-white rounded-2xl rounded-tr-sm py-3 px-5 text-sm font-medium shadow-md">
                        ${this.t('mockupChat', 'prompt2')}
                      </div>
                    </div>

                    <div class="flex gap-4 max-w-[90%] mt-2">
                      <div class="w-8 h-8 rounded-full bg-[#EAF3FB] flex items-center justify-center flex-shrink-0 p-1.5">
                         <img src="${OWL_LOGO}" class="w-full h-full object-contain" />
                      </div>
                      <div class="bg-[#F6FAFE] border border-blue-50 text-slate-700 rounded-2xl rounded-tl-sm py-3 px-5 text-sm leading-relaxed">
                        ${this.t('mockups', 'upload', 'extracting')}
                      </div>
                    </div>
                  </div>

                  <div class="p-4 border-t border-gray-100 bg-white">
                    <div class="flex items-center gap-2 relative">
                      <div class="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full">${unsafeSVG(Paperclip)}</div>
                      <input type="text" disabled placeholder="${this.t('mockupChat', 'placeholder')}" class="w-full bg-[#F8FAFC] border border-gray-200 text-sm rounded-full py-3 pl-12 pr-12 focus:outline-none cursor-not-allowed" />
                      <div class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-[#89B4D6] rounded-full flex items-center justify-center text-white">
                        <div class="w-4 h-4 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full -ml-0.5">${unsafeSVG(Send)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    `;
  }
}